// @ts-nocheck
import { supabase } from '@/integrations/supabase/client';

interface TeamStanding {
  teamId: string;
  teamName: string;
  poolId: string;
  wins: number;
  losses: number;
  ties: number;
  pointsFor: number;
  pointsAgainst: number;
  pointsDiff: number;
  standingPoints: number; // 3 for win, 1 for tie, 0 for loss
}

interface PoolMatch {
  id: string;
  team1_id: string;
  team2_id: string;
  team1_name: string;
  team2_name: string;
  team1_score: number;
  team2_score: number;
  pool_id: string;
  pool_number: number;
  status: string;
}

/**
 * Calculate standings for all pools in a tournament
 */
export const calculatePoolStandings = async (tournamentId: string): Promise<Map<string, TeamStanding[]>> => {
  console.log('[POOL] Calculating standings for tournament:', tournamentId);
  
  // Get all pool matches for this tournament
  const { data: poolMatches, error } = await supabase
    .from('tournament_matches')
    .select('*')
    .eq('tournament_id', tournamentId)
    .eq('bracket_type', 'pool')
    .order('pool_number', { ascending: true })
    .order('match_number', { ascending: true });

  if (error) {
    console.error('[POOL] Error fetching pool matches:', error);
    throw error;
  }

  console.log('[POOL] Found pool matches:', poolMatches?.length);

  // Fetch official pool records from tournament_pools
  const { data: poolRecords } = await supabase
    .from('tournament_pools')
    .select('id, name, pool_order')
    .eq('tournament_id', tournamentId)
    .order('pool_order');

  // Build a map from pool_order -> pool name for display
  const poolOrderToName = new Map<number, string>();
  poolRecords?.forEach(p => poolOrderToName.set(p.pool_order, p.name));

  // Group matches by pool_number (correlates with pool_order)
  const poolGroups = new Map<string, PoolMatch[]>();
  poolMatches?.forEach(match => {
    // Use pool_order-based key if pool records exist, otherwise fallback
    const poolNumber = match.pool_number || 1;
    const poolKey = poolOrderToName.get(poolNumber) || match.pool_id || `pool_${poolNumber}`;
    if (!poolGroups.has(poolKey)) {
      poolGroups.set(poolKey, []);
    }
    poolGroups.get(poolKey)!.push(match as PoolMatch);
  });

  // Calculate standings for each pool
  const poolStandings = new Map<string, TeamStanding[]>();
  
  poolGroups.forEach((matches, poolId) => {
    const teamStats = new Map<string, TeamStanding>();
    
    // Initialize teams from matches
    matches.forEach(match => {
      if (match.team1_id && !teamStats.has(match.team1_id)) {
        teamStats.set(match.team1_id, {
          teamId: match.team1_id,
          teamName: match.team1_name || 'TBD',
          poolId,
          wins: 0,
          losses: 0,
          ties: 0,
          pointsFor: 0,
          pointsAgainst: 0,
          pointsDiff: 0,
          standingPoints: 0,
        });
      }
      if (match.team2_id && !teamStats.has(match.team2_id)) {
        teamStats.set(match.team2_id, {
          teamId: match.team2_id,
          teamName: match.team2_name || 'TBD',
          poolId,
          wins: 0,
          losses: 0,
          ties: 0,
          pointsFor: 0,
          pointsAgainst: 0,
          pointsDiff: 0,
          standingPoints: 0,
        });
      }
    });

    // Calculate stats from completed matches
    matches.forEach(match => {
      if (match.status === 'completed' && match.team1_id && match.team2_id) {
        const team1Stats = teamStats.get(match.team1_id)!;
        const team2Stats = teamStats.get(match.team2_id)!;
        
        const score1 = match.team1_score || 0;
        const score2 = match.team2_score || 0;
        
        team1Stats.pointsFor += score1;
        team1Stats.pointsAgainst += score2;
        team2Stats.pointsFor += score2;
        team2Stats.pointsAgainst += score1;
        
        if (score1 > score2) {
          team1Stats.wins++;
          team1Stats.standingPoints += 3;
          team2Stats.losses++;
        } else if (score2 > score1) {
          team2Stats.wins++;
          team2Stats.standingPoints += 3;
          team1Stats.losses++;
        } else {
          team1Stats.ties++;
          team1Stats.standingPoints += 1;
          team2Stats.ties++;
          team2Stats.standingPoints += 1;
        }
        
        team1Stats.pointsDiff = team1Stats.pointsFor - team1Stats.pointsAgainst;
        team2Stats.pointsDiff = team2Stats.pointsFor - team2Stats.pointsAgainst;
      }
    });

    // Sort by standing points (desc), then point differential (desc), then points for (desc)
    const sorted = Array.from(teamStats.values()).sort((a, b) => {
      if (b.standingPoints !== a.standingPoints) return b.standingPoints - a.standingPoints;
      if (b.pointsDiff !== a.pointsDiff) return b.pointsDiff - a.pointsDiff;
      return b.pointsFor - a.pointsFor;
    });
    
    poolStandings.set(poolId, sorted);
  });

  return poolStandings;
};

/**
 * Check if all pool play matches are completed
 */
export const isPoolPlayComplete = async (tournamentId: string): Promise<{ complete: boolean; completed: number; total: number }> => {
  const { data: poolMatches, error } = await supabase
    .from('tournament_matches')
    .select('status')
    .eq('tournament_id', tournamentId)
    .eq('bracket_type', 'pool');

  if (error) throw error;

  const total = poolMatches?.length || 0;
  const completed = poolMatches?.filter(m => m.status === 'completed').length || 0;

  return {
    complete: total > 0 && completed === total,
    completed,
    total,
  };
};

/**
 * Finalize pool play and populate playoff bracket with advancing teams
 */
export const finalizePoolPlayAndPopulatePlayoffs = async (
  tournamentId: string,
  teamsPerPool: number = 2 // Top N teams from each pool advance
): Promise<{ success: boolean; message: string; advancingTeams?: string[] }> => {
  console.log('[POOL] Finalizing pool play for tournament:', tournamentId, 'teams per pool:', teamsPerPool);

  try {
    // Check if pool play is complete
    const poolStatus = await isPoolPlayComplete(tournamentId);
    if (!poolStatus.complete) {
      return {
        success: false,
        message: `Pool play is not complete. ${poolStatus.completed}/${poolStatus.total} matches completed.`,
      };
    }

    // Calculate final standings
    const standings = await calculatePoolStandings(tournamentId);
    console.log('[POOL] Calculated standings for pools:', standings.size);

    // Get advancing teams from each pool
    const advancingTeams: { teamId: string; teamName: string; seed: number; poolId: string }[] = [];
    let seedCounter = 1;

    // Sort pools for consistent ordering
    const sortedPools = Array.from(standings.entries()).sort(([a], [b]) => a.localeCompare(b));

    // Cross-seeding: alternate picks from each pool for fair bracket placement
    // E.g., Pool A #1, Pool B #1, Pool A #2, Pool B #2 for 2 pools with 2 advancing each
    for (let position = 0; position < teamsPerPool; position++) {
      sortedPools.forEach(([poolId, poolTeams]) => {
        if (poolTeams[position]) {
          advancingTeams.push({
            teamId: poolTeams[position].teamId,
            teamName: poolTeams[position].teamName,
            seed: seedCounter++,
            poolId,
          });
        }
      });
    }

    console.log('[POOL] Advancing teams:', advancingTeams.map(t => `${t.seed}. ${t.teamName} (${t.poolId})`));

    // Get playoff bracket matches (winners bracket, not pool)
    const { data: playoffMatches, error: matchError } = await supabase
      .from('tournament_matches')
      .select('*')
      .eq('tournament_id', tournamentId)
      .in('bracket_type', ['winners', 'final'])
      .order('round_number', { ascending: true })
      .order('match_number', { ascending: true });

    if (matchError) throw matchError;

    if (!playoffMatches || playoffMatches.length === 0) {
      return {
        success: false,
        message: 'No playoff bracket matches found. Generate bracket first.',
      };
    }

    console.log('[POOL] Found playoff matches:', playoffMatches.length);

    // Get first round matches (lowest round number in playoffs)
    const minRound = Math.min(...playoffMatches.map(m => m.round_number));
    const firstRoundMatches = playoffMatches.filter(m => m.round_number === minRound);

    console.log('[POOL] First round matches:', firstRoundMatches.length);

    // Assign teams to first round using standard bracket seeding
    // 1 vs 8, 4 vs 5, 3 vs 6, 2 vs 7 for 8-team bracket
    const bracketSeeding = generateBracketSeeding(advancingTeams.length);
    
    for (let i = 0; i < firstRoundMatches.length && i < bracketSeeding.length; i++) {
      const matchup = bracketSeeding[i];
      const team1 = advancingTeams.find(t => t.seed === matchup[0]);
      const team2 = advancingTeams.find(t => t.seed === matchup[1]);

      const updateData: any = {};
      if (team1) {
        updateData.team1_id = team1.teamId;
        updateData.team1_name = team1.teamName;
      }
      if (team2) {
        updateData.team2_id = team2.teamId;
        updateData.team2_name = team2.teamName;
      }

      if (Object.keys(updateData).length > 0) {
        const { error: updateError } = await supabase
          .from('tournament_matches')
          .update(updateData)
          .eq('id', firstRoundMatches[i].id);

        if (updateError) {
          console.error('[POOL] Error updating match:', firstRoundMatches[i].id, updateError);
        } else {
          console.log('[POOL] Updated match', firstRoundMatches[i].match_number, 'with', team1?.teamName, 'vs', team2?.teamName);
        }
      }
    }

    return {
      success: true,
      message: `Pool play finalized! ${advancingTeams.length} teams advanced to playoffs.`,
      advancingTeams: advancingTeams.map(t => t.teamName),
    };
  } catch (error) {
    console.error('[POOL] Finalization error:', error);
    return {
      success: false,
      message: `Error finalizing pool play: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
};

/**
 * Generate standard bracket seeding matchups
 * For 8 teams: [[1,8], [4,5], [3,6], [2,7]]
 * For 4 teams: [[1,4], [2,3]]
 */
const generateBracketSeeding = (teamCount: number): [number, number][] => {
  if (teamCount <= 2) return [[1, 2]];
  if (teamCount <= 4) return [[1, 4], [2, 3]];
  if (teamCount <= 8) return [[1, 8], [4, 5], [3, 6], [2, 7]];
  if (teamCount <= 16) {
    return [
      [1, 16], [8, 9], [4, 13], [5, 12],
      [3, 14], [6, 11], [7, 10], [2, 15]
    ];
  }
  
  // For larger brackets, use a simple pairing
  const matchups: [number, number][] = [];
  for (let i = 1; i <= teamCount / 2; i++) {
    matchups.push([i, teamCount - i + 1]);
  }
  return matchups;
};

/**
 * Get pool play status summary for display
 */
export const getPoolPlayStatus = async (tournamentId: string) => {
  const poolStatus = await isPoolPlayComplete(tournamentId);
  const standings = await calculatePoolStandings(tournamentId);

  return {
    ...poolStatus,
    poolCount: standings.size,
    standings: Object.fromEntries(standings),
  };
};
